import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ADVANCE_CHECK_LABELS,
  ADVANCE_CLOSURE_LABELS,
  type AdvanceCheck,
  type AdvanceFixesResult,
  ADVANCE_STATE_LABELS,
  type AdvanceCandidate,
  type AdvanceInvoice,
  type AdvanceInvoicesRequest,
  type AdvanceResponse,
  type AdvancesResponse,
  type AdvanceState,
  formatMoney,
  fromCents,
  parseDecimal,
  toCents,
} from '@k-management/shared';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { amountSort, SortHeader, useTableSort } from '../../components/SortHeader.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { movementPath } from './treasury-ui.js';
import { useListFilters } from '../../lib/list-filters.js';

const advancesKey = ['treasury', 'advances'] as const;

type StateFilter = 'open' | 'all' | AdvanceState;

const STATE_PILLS: Record<AdvanceState, string> = {
  'da-collegare': 'pill warn',
  aperto: 'pill info',
  'in-parte': 'pill warn',
  chiuso: 'pill ok',
  insoluto: 'pill bad',
};

/**
 * Tesoreria › Anticipi fatture (1.62): the advances of the banks on the issued
 * invoices, read from the movements. What is owed to each bank, every advance
 * with its invoices, the repayments and unpaid that cite them; a person only
 * says which invoices an advance was given on.
 */
export function AdvancesPage() {
  usePageTitle('Anticipi fatture');
  const canWrite = useCan('tesoreria.write');
  const data = useQuery({
    queryKey: advancesKey,
    queryFn: () => apiRequest<AdvancesResponse>('/treasury/advances'),
  });
  const { filters, setFilter, reset, changed, changedClass } = useListFilters('advances', {
    state: 'open' as StateFilter,
    account: 'all' as string,
  });
  const [opened, setOpened] = useState<string | null>(null);
  const [linking, setLinking] = useState<AdvanceResponse | null>(null);
  const [retaining, setRetaining] = useState<{
    advance: AdvanceResponse;
    invoice: AdvanceInvoice;
  } | null>(null);
  const queryClient = useQueryClient();
  // Intesa (1.63): an invoice collected by the bank, which kept it for its advance.
  const retain = useMutation({
    mutationFn: ({
      movementId,
      invoiceId,
      date,
      amount,
    }: {
      movementId: string;
      invoiceId: string;
      date: string;
      amount: string;
    }) =>
      apiRequest<AdvancesResponse>(
        `/treasury/advances/${movementId}/invoices/${invoiceId}/retained`,
        { method: 'POST', body: { date, amount } },
      ),
    onSuccess: async (updated) => {
      queryClient.setQueryData(advancesKey, updated);
      await queryClient.invalidateQueries({ queryKey: ['treasury'] });
    },
  });
  // The open invoices of an advance whose collection the ledger dates.
  const retainable = (advance: AdvanceResponse) =>
    advance.invoices.filter((invoice) => toCents(invoice.open) > 0 && invoice.collectedOn);
  const retainAll = async (movementId: string, invoices: AdvanceInvoice[]) => {
    for (const invoice of invoices) {
      await retain.mutateAsync({
        movementId,
        invoiceId: invoice.invoiceId,
        date: invoice.collectedOn!,
        amount: invoice.open,
      });
    }
  };

  // The corrections exact to the cent apply by themselves, once the page is open (12/10/2026).
  const fixes = useMutation({
    mutationFn: () =>
      apiRequest<AdvanceFixesResult>('/treasury/advances/checks/apply', { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['treasury'] });
    },
  });
  const fixing = useRef(false);
  const fixable = data.data?.checks.some((check) => check.fix) ?? false;
  useEffect(() => {
    if (!canWrite || !fixable || fixing.current) return;
    fixing.current = true;
    fixes.mutate();
  }, [canWrite, fixable, fixes]);

  // Before the early returns, the sorts being hooks.
  const accountName = (code: string) =>
    data.data?.accounts.find((account) => account.code === code)?.name ?? code;
  const shown = (data.data?.advances ?? []).filter(
    (item) =>
      (filters.account === 'all' || item.accountCode === filters.account) &&
      (filters.state === 'all' ||
        (filters.state === 'open'
          ? item.state !== 'chiuso' && item.state !== 'insoluto'
          : item.state === filters.state)),
  );
  const sorting = useTableSort('advances.list', shown, {
    date: (item) => item.date,
    bank: (item) => accountName(item.accountCode),
    reference: (item) => item.reference,
    presented: (item) => amountSort(item.presented),
    advanced: (item) => amountSort(item.advanced),
    closed: (item) => amountSort(item.closed),
    open: (item) => amountSort(item.open),
    state: (item) => ADVANCE_STATE_LABELS[item.state],
  });
  const unmatchedSorting = useTableSort('advances.unmatched', data.data?.unmatched ?? [], {
    date: (item) => item.date,
    bank: (item) => accountName(item.accountCode),
    kind: (item) => (item.kind === 'insoluto' ? 'Insoluto' : 'Rimborso'),
    invoice: (item) => item.invoiceNumber,
    amount: (item) => amountSort(item.amount),
  });

  if (data.isPending) return <p className="muted">Caricamento…</p>;
  if (data.isError) return <p className="error">{data.error.message}</p>;
  const { accounts, advances, unmatched, checks } = data.data;
  const toLook = checks.filter((check) => !check.fix);
  const debt = accounts.reduce((sum, account) => sum + toCents(account.debt), 0);

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Anticipi fatture</h1>
          <p className="muted">
            Gli anticipi delle banche sulle fatture emesse, letti dai movimenti: l’anticipo, i
            rimborsi quando il cliente paga, gli insoluti. È un prestito, non un incasso: la fattura
            si incassa quando paga il cliente. Per ogni anticipo si indicano le fatture presentate.
          </p>
        </div>
      </div>

      <div className="treasury-tiles">
        <div className="treasury-tile">
          <span>Debito per anticipi oggi</span>
          <strong>{formatMoney(fromCents(debt))}</strong>
        </div>
        {accounts.map((account) => (
          <div className="treasury-tile" key={account.code}>
            <span>{account.name}</span>
            <strong>{formatMoney(account.debt)}</strong>
            <small className="muted">
              {account.open} {account.open === 1 ? 'anticipo aperto' : 'anticipi aperti'}
            </small>
          </div>
        ))}
      </div>

      {fixes.data && fixes.data.applied.length > 0 && (
        <div className="notice info">
          <strong>
            {fixes.data.applied.length === 1
              ? 'Sistemata da sola 1 incongruenza'
              : `Sistemate da sole ${fixes.data.applied.length} incongruenze`}
          </strong>{' '}
          (calcolo esatto al centesimo, nel registro attività):
          <ul className="small">
            {fixes.data.applied.map((check, index) => (
              <li key={index}>
                <span className="pill info">{ADVANCE_CHECK_LABELS[check.kind]}</span> {check.text}
              </li>
            ))}
          </ul>
        </div>
      )}
      {fixes.error && <p className="error">{fixes.error.message}</p>}
      {toLook.length > 0 && (
        <ChecksBox
          checks={toLook}
          onLink={
            canWrite
              ? (advanceId) => {
                  const found = advances.find((item) => item.movementId === advanceId);
                  if (found) setLinking(found);
                }
              : null
          }
          onRetain={
            canWrite
              ? (advanceId, invoiceId) => {
                  const found = advances.find((item) => item.movementId === advanceId);
                  const invoice = found?.invoices.find((item) => item.invoiceId === invoiceId);
                  if (found && invoice) setRetaining({ advance: found, invoice });
                }
              : null
          }
        />
      )}

      <div className="card stack">
        <div className="row">
          <select
            className={changedClass('state')}
            aria-label="Stato"
            value={filters.state}
            onChange={(event) => setFilter('state', event.target.value as StateFilter)}
          >
            <option value="open">Aperti</option>
            {(Object.keys(ADVANCE_STATE_LABELS) as AdvanceState[]).map((state) => (
              <option key={state} value={state}>
                {ADVANCE_STATE_LABELS[state]}
              </option>
            ))}
            <option value="all">Tutti</option>
          </select>
          <select
            className={changedClass('account')}
            aria-label="Banca"
            value={filters.account}
            onChange={(event) => setFilter('account', event.target.value)}
          >
            <option value="all">Tutte le banche</option>
            {accounts.map((account) => (
              <option key={account.code} value={account.code}>
                {account.name}
              </option>
            ))}
          </select>
          {changed && (
            <button type="button" className="small" onClick={reset}>
              Azzera filtri
            </button>
          )}
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <SortHeader sort={sorting} column="date">
                  Data
                </SortHeader>
                <SortHeader sort={sorting} column="bank">
                  Banca
                </SortHeader>
                <SortHeader sort={sorting} column="reference">
                  Riferimento
                </SortHeader>
                <th>Fatture</th>
                <SortHeader sort={sorting} column="presented" numeric>
                  Presentato
                </SortHeader>
                <SortHeader sort={sorting} column="advanced" numeric>
                  Anticipato
                </SortHeader>
                <SortHeader sort={sorting} column="closed" numeric>
                  Rimborsato
                </SortHeader>
                <SortHeader sort={sorting} column="open" numeric>
                  Ancora dovuto
                </SortHeader>
                <SortHeader sort={sorting} column="state">
                  Stato
                </SortHeader>
                {canWrite && <th />}
              </tr>
            </thead>
            <tbody>
              {sorting.rows.map((item) => (
                <Fragment key={item.movementId}>
                  <tr
                    style={{ cursor: item.invoices.length ? 'pointer' : undefined }}
                    onClick={() => setOpened(opened === item.movementId ? null : item.movementId)}
                  >
                    <td>{formatDate(item.date)}</td>
                    <td>
                      <Link
                        to={movementPath(item.accountCode, item.movementId)}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {accountName(item.accountCode)}
                      </Link>
                    </td>
                    <td>{item.reference ?? '—'}</td>
                    <td>
                      {item.invoices.length
                        ? item.invoices
                            .map((invoice) => `${invoice.number} · ${invoice.counterpartyName}`)
                            .join(', ')
                        : '—'}
                    </td>
                    <td className="numeric">
                      {item.presented ? formatMoney(item.presented) : '—'}
                    </td>
                    <td className="numeric">{formatMoney(item.advanced)}</td>
                    <td className="numeric">{formatMoney(item.closed)}</td>
                    <td className="numeric">
                      <strong>{formatMoney(item.open)}</strong>
                    </td>
                    <td>
                      <span className={STATE_PILLS[item.state]}>
                        {ADVANCE_STATE_LABELS[item.state]}
                      </span>
                    </td>
                    {canWrite && (
                      <td className="numeric">
                        <button
                          type="button"
                          className="link"
                          onClick={(event) => {
                            event.stopPropagation();
                            setLinking(item);
                          }}
                        >
                          {item.invoices.length ? 'Cambia le fatture' : 'Indica le fatture'}
                        </button>
                      </td>
                    )}
                  </tr>
                  {opened === item.movementId &&
                    (item.invoices.length > 0 || item.extinctions.length > 0) && (
                      <tr>
                        <td colSpan={canWrite ? 10 : 9}>
                          {item.extinctions.length > 0 && (
                            <p className="muted small">
                              Estinto da noi:{' '}
                              {item.extinctions
                                .map(
                                  (closure) =>
                                    `${formatDate(closure.date)} ${formatMoney(closure.amount)}`,
                                )
                                .join(' · ')}
                            </p>
                          )}
                          {canWrite && retainable(item).length > 0 && (
                            <div className="row">
                              <span className="muted small">
                                Il cliente ha pagato alla banca e sul conto non è arrivato nulla?
                              </span>
                              <button
                                type="button"
                                className="small"
                                disabled={retain.isPending}
                                onClick={() => void retainAll(item.movementId, retainable(item))}
                              >
                                Segna incassate dalla banca ({retainable(item).length}) alle date
                                del mastrino
                              </button>
                            </div>
                          )}
                          {retain.error && <p className="error">{retain.error.message}</p>}
                          <AdvanceInvoicesTable
                            advance={item}
                            canWrite={canWrite}
                            busy={retain.isPending}
                            onRetain={(invoice) => setRetaining({ advance: item, invoice })}
                          />
                        </td>
                      </tr>
                    )}
                </Fragment>
              ))}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={canWrite ? 10 : 9} className="muted">
                    {advances.length
                      ? 'Nessun anticipo con questi filtri.'
                      : 'Nessun anticipo nei movimenti caricati.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {unmatched.length > 0 && (
        <div className="card stack">
          <h2>Rimborsi e insoluti da abbinare ({unmatched.length})</h2>
          <p className="muted small">
            La banca cita una fattura che non è ancora in un anticipo: si abbinano da soli quando
            all’anticipo si indicano le sue fatture.
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader sort={unmatchedSorting} column="date">
                    Data
                  </SortHeader>
                  <SortHeader sort={unmatchedSorting} column="bank">
                    Banca
                  </SortHeader>
                  <SortHeader sort={unmatchedSorting} column="kind">
                    Cosa
                  </SortHeader>
                  <SortHeader sort={unmatchedSorting} column="invoice">
                    Fattura citata
                  </SortHeader>
                  <SortHeader sort={unmatchedSorting} column="amount" numeric>
                    Importo
                  </SortHeader>
                </tr>
              </thead>
              <tbody>
                {unmatchedSorting.rows.map((item) => (
                  <tr key={item.movementId}>
                    <td>{formatDate(item.date)}</td>
                    <td>{accountName(item.accountCode)}</td>
                    <td>{item.kind === 'insoluto' ? 'Insoluto' : 'Rimborso'}</td>
                    <td>
                      {item.invoiceNumber}
                      {item.counterparty && (
                        <small className="muted block">{item.counterparty}</small>
                      )}
                    </td>
                    <td className="numeric">{formatMoney(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {linking && <LinkDialog advance={linking} onClose={() => setLinking(null)} />}
      {retaining && (
        <RetainDialog
          {...retaining}
          busy={retain.isPending}
          error={retain.error?.message}
          onClose={() => setRetaining(null)}
          onConfirm={(date, amount) =>
            retain.mutate(
              {
                movementId: retaining.advance.movementId,
                invoiceId: retaining.invoice.invoiceId,
                date,
                amount,
              },
              { onSuccess: () => setRetaining(null) },
            )
          }
        />
      )}
    </section>
  );
}

/** The invoices of an advance, under its row (a component of its own for the sort). */
function AdvanceInvoicesTable({
  advance,
  canWrite,
  busy,
  onRetain,
}: {
  advance: AdvanceResponse;
  canWrite: boolean;
  busy: boolean;
  onRetain: (invoice: AdvanceInvoice) => void;
}) {
  const sorting = useTableSort('advances.invoices', advance.invoices, {
    invoice: (invoice) => invoice.number,
    presented: (invoice) => amountSort(invoice.presented),
    advanced: (invoice) => amountSort(invoice.advanced),
    open: (invoice) => amountSort(invoice.open),
  });
  return (
    <table className="data-table">
      <thead>
        <tr>
          <SortHeader sort={sorting} column="invoice">
            Fattura
          </SortHeader>
          <SortHeader sort={sorting} column="presented" numeric>
            Presentato
          </SortHeader>
          <SortHeader sort={sorting} column="advanced" numeric>
            Anticipato
          </SortHeader>
          <th>Restituito alla banca</th>
          <SortHeader sort={sorting} column="open" numeric>
            Ancora dovuto
          </SortHeader>
        </tr>
      </thead>
      <tbody>
        {sorting.rows.map((invoice) => (
          <tr key={invoice.invoiceId}>
            <td>
              <Link to={`/tesoreria/fatture/${invoice.invoiceId}`}>{invoice.number}</Link> del{' '}
              {formatDate(invoice.date)} · {invoice.counterpartyName}
            </td>
            <td className="numeric">{formatMoney(invoice.presented)}</td>
            <td className="numeric">{formatMoney(invoice.advanced)}</td>
            <td>
              {invoice.closures.length === 0
                ? '—'
                : invoice.closures.map((closure) => (
                    <span key={closure.movementId} className="block">
                      {formatDate(closure.date)} · {ADVANCE_CLOSURE_LABELS[closure.kind]}
                      {closure.fromInvoice && ` della FT ${closure.fromInvoice.number}`} ·{' '}
                      {formatMoney(closure.amount)}
                    </span>
                  ))}
              {canWrite && toCents(invoice.open) > 0 && (
                <button
                  type="button"
                  className="link small"
                  disabled={busy}
                  onClick={() => onRetain(invoice)}
                >
                  Incassata dalla banca…
                </button>
              )}
            </td>
            <td className="numeric">{formatMoney(invoice.open)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Intesa (1.63): the customer paid the bank, which kept what it had advanced:
 * a collection of the invoice without a movement. The day proposed is the one
 * of the ledger of the customer, when there.
 */
function RetainDialog({
  advance,
  invoice,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  advance: AdvanceResponse;
  invoice: AdvanceInvoice;
  busy: boolean;
  error?: string;
  onClose: () => void;
  onConfirm: (date: string, amount: string) => void;
}) {
  const [date, setDate] = useState(invoice.collectedOn ?? advance.date);
  const [amount, setAmount] = useState(invoice.open);
  const number = parseDecimal(amount);
  return (
    <ConfirmDialog
      title={`Fattura ${invoice.number} incassata dalla banca`}
      confirmLabel="Segna incassata"
      busy={busy}
      disabled={!number || !date}
      error={error}
      onConfirm={() => onConfirm(date, number!.toFixed(2))}
      onCancel={onClose}
    >
      <p>
        Il cliente ha pagato alla banca, che ha trattenuto quanto aveva anticipato: è un incasso
        della fattura senza movimento sul conto, e chiude l’anticipo.
      </p>
      <Field
        label="Data"
        hint={
          invoice.collectedOn
            ? 'Dal mastrino del cliente («Incasso Ft …»).'
            : 'Nel mastrino non c’è l’incasso: indica la data che conosci.'
        }
      >
        <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
      </Field>
      <Field label="Trattenuto dalla banca">
        <DecimalInput value={amount} onChange={setAmount} decimals={[2, 2]} />
      </Field>
    </ConfirmDialog>
  );
}

/**
 * Which invoices an advance was given on: the issued invoices of the year
 * before it, those that add up to what the bank says were presented already
 * ticked; for each, what was presented (its net due, or its total with the
 * VAT when that is what adds up, unless changed).
 */
function LinkDialog({ advance, onClose }: { advance: AdvanceResponse; onClose: () => void }) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const candidates = useQuery({
    queryKey: [...advancesKey, advance.movementId, 'candidates'],
    queryFn: () =>
      apiRequest<AdvanceCandidate[]>(`/treasury/advances/${advance.movementId}/candidates`),
  });
  const [chosen, setChosen] = useState<Record<string, string> | null>(null);
  const [search, setSearch] = useState('');
  // At first: the invoices already linked, else those proposed.
  const initial = (): Record<string, string> =>
    advance.invoices.length
      ? Object.fromEntries(advance.invoices.map((item) => [item.invoiceId, item.presented]))
      : Object.fromEntries(
          (candidates.data ?? [])
            .filter((item) => item.proposed)
            .map((item) => [item.id, item.presented]),
        );
  const selected = chosen ?? (candidates.data ? initial() : {});
  const total = Object.values(selected).reduce(
    (sum, value) => sum + Math.round((parseDecimal(value) ?? 0) * 100),
    0,
  );
  const presented = advance.presented ? toCents(advance.presented) : null;
  const save = useMutation({
    mutationFn: () =>
      apiRequest<AdvancesResponse>(`/treasury/advances/${advance.movementId}/invoices`, {
        method: 'PUT',
        body: {
          invoices: Object.entries(selected).map(([invoiceId, value]) => ({
            invoiceId,
            presented: (parseDecimal(value) ?? 0).toFixed(2),
          })),
        } satisfies AdvanceInvoicesRequest,
      }),
    onSuccess: async (updated) => {
      queryClient.setQueryData(advancesKey, updated);
      await queryClient.invalidateQueries({ queryKey: ['treasury'] });
      onClose();
    },
  });
  const unlink = useMutation({
    mutationFn: () =>
      apiRequest<AdvancesResponse>(`/treasury/advances/${advance.movementId}/invoices`, {
        method: 'DELETE',
      }),
    onSuccess: async (updated) => {
      queryClient.setQueryData(advancesKey, updated);
      await queryClient.invalidateQueries({ queryKey: ['treasury'] });
      onClose();
    },
  });
  const needle = search.trim().toLowerCase();
  const list = (candidates.data ?? []).filter(
    (item) =>
      item.id in selected ||
      !needle ||
      `${item.number} ${item.counterpartyName}`.toLowerCase().includes(needle),
  );
  const sorting = useTableSort('advances.link-invoices', list, {
    invoice: (item) => item.number,
    netDue: (item) => amountSort(item.netDue),
  });
  const toggle = (item: AdvanceCandidate) => {
    const next = { ...selected };
    if (item.id in next) delete next[item.id];
    else next[item.id] = item.presented;
    setChosen(next);
  };

  return (
    <dialog ref={dialogRef} className="dialog wide-dialog" onClose={onClose}>
      <div className="stack">
        <h2>Fatture dell’anticipo del {formatDate(advance.date)}</h2>
        <p className="muted">
          {advance.reference ?? 'Anticipo'} · anticipati{' '}
          <strong>{formatMoney(advance.advanced)}</strong>
          {advance.presented && (
            <>
              {' '}
              su <strong>{formatMoney(advance.presented)}</strong> presentati
            </>
          )}
          . L’anticipato si divide sulle fatture in proporzione a quanto presentato.
        </p>
        <input
          type="search"
          placeholder="Numero o cliente"
          aria-label="Cerca una fattura"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {candidates.isPending && <p className="muted">Caricamento…</p>}
        {candidates.isError && <p className="error">{candidates.error.message}</p>}
        {candidates.data && (
          <div className="table-wrap" style={{ maxHeight: '45vh' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader sort={sorting} column="invoice">
                    Fattura
                  </SortHeader>
                  <SortHeader sort={sorting} column="netDue" numeric>
                    Netto
                  </SortHeader>
                  <th className="numeric">Presentato</th>
                </tr>
              </thead>
              <tbody>
                {sorting.rows.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={item.id in selected}
                          onChange={() => toggle(item)}
                        />{' '}
                        {item.number} del {formatDate(item.date)} · {item.counterpartyName}
                      </label>
                      {item.proposed && <span className="pill info">la somma torna</span>}
                    </td>
                    <td className="numeric">{formatMoney(item.netDue)}</td>
                    <td className="numeric">
                      {item.id in selected ? (
                        <DecimalInput
                          aria-label={`Presentato della fattura ${item.number}`}
                          value={selected[item.id]!}
                          onChange={(value) => setChosen({ ...selected, [item.id]: value })}
                          decimals={[2, 2]}
                        />
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr>
                    <td colSpan={3} className="muted">
                      Nessuna fattura emessa aperta nell’anno prima dell’anticipo.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <p className={presented !== null && total !== presented ? 'notice small' : 'muted small'}>
          Scelte: {formatMoney(fromCents(total))}
          {presented !== null &&
            (total === presented
              ? ' · tornano con quanto presentato.'
              : total > presented
                ? ` · più dei ${formatMoney(fromCents(presented))} presentati.`
                : ` · mancano ${formatMoney(fromCents(presented - total))} ai presentati (una fattura non in K-Management?).`)}
        </p>
        {(save.error ?? unlink.error) && (
          <p className="error">{(save.error ?? unlink.error)!.message}</p>
        )}
        <div className="actions">
          {advance.invoices.length > 0 && (
            <button type="button" disabled={unlink.isPending} onClick={() => unlink.mutate()}>
              Togli le fatture
            </button>
          )}
          <span className="grid-spacer" />
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={
              save.isPending ||
              Object.keys(selected).length === 0 ||
              (presented !== null && total > presented)
            }
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

/**
 * «Da sistemare» (12/10/2026): what does not agree and needs a person, with
 * where to look; an advance without invoices or open beyond the due date
 * (2.09) with what to do.
 */
function ChecksBox({
  checks,
  onLink,
  onRetain,
}: {
  checks: AdvanceCheck[];
  onLink: ((advanceId: string) => void) | null;
  onRetain: ((advanceId: string, invoiceId: string) => void) | null;
}) {
  return (
    <div className="notice">
      <strong>
        Da sistemare:{' '}
        {checks.length === 1 ? '1 controllo non torna' : `${checks.length} controlli non tornano`}
      </strong>
      <div className="table-wrap">
        <table className="data-table">
          <tbody>
            {checks.map((check, index) => (
              <tr key={index}>
                <td>
                  <span className="pill warn">{ADVANCE_CHECK_LABELS[check.kind]}</span>
                  <small className="muted block">
                    {check.number && check.invoiceId ? (
                      <Link to={`/tesoreria/fatture/${check.invoiceId}`}>FT {check.number}</Link>
                    ) : null}
                    {check.counterpartyName ? ` · ${check.counterpartyName}` : ''}
                  </small>
                </td>
                <td>{check.text}</td>
                <td>
                  {check.kind === 'da-indicare' && check.advanceId && onLink && (
                    <button
                      type="button"
                      className="link small"
                      onClick={() => onLink(check.advanceId!)}
                    >
                      Indica le fatture…
                    </button>
                  )}
                  {check.kind === 'scaduto' && check.advanceId && check.invoiceId && onRetain && (
                    <button
                      type="button"
                      className="link small"
                      onClick={() => onRetain(check.advanceId!, check.invoiceId!)}
                    >
                      Incassata dalla banca…
                    </button>
                  )}
                  {check.movement && (
                    <a
                      href={`/tesoreria/banche/${encodeURIComponent(check.movement.accountCode)}/movimenti/${check.movement.id}/riconcilia`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Riconcilia il bonifico del {formatDate(check.movement.date)}
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
